import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Building2, Check, CreditCard, ExternalLink, FileText, Lightbulb, PartyPopper, UserPlus, Users } from 'lucide-react';
import { FinancingRequestForm } from './FinancingRequestForm';
import { AddLearnerModal } from '../enterprise/AddLearnerModal';
import { formatPriceFromCents } from '@/utils/formatPrice';
import { logger } from '@/lib/logger';

interface EnterprisePaymentOptionsProps {
  courseId: string;
  formationTitle: string;
  price: number;
  currency?: string;
  isCart?: boolean;
  cartItems?: Array<{id: string; title: string; price: number; currency: string}>;
  isEmployeeRegistration?: boolean;
  multiLearnerData?: {
    learners: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      position?: string;
    }>;
    company: any;
    startDate?: Date;
  };
  fallbackUser?: User | null; // Fallback pour les utilisateurs fraîchement créés
}

export function EnterprisePaymentOptions({ 
  courseId, 
  formationTitle, 
  price, 
  currency = "eur", 
  isCart = false, 
  cartItems = [],
  isEmployeeRegistration = false,
  multiLearnerData,
  fallbackUser
}: EnterprisePaymentOptionsProps) {
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [showFinancingForm, setShowFinancingForm] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);
  const [fallbackPaymentUrl, setFallbackPaymentUrl] = useState<string | null>(null);
  const [learnerCount, setLearnerCount] = useState(
    isEmployeeRegistration && multiLearnerData ? multiLearnerData.learners.length : 1
  );
  const [actualLearnerCount, setActualLearnerCount] = useState(
    isEmployeeRegistration && multiLearnerData ? multiLearnerData.learners.length : 1
  );
  const [savedLearners, setSavedLearners] = useState<Array<{
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    position?: string;
  }>>(isEmployeeRegistration && multiLearnerData ? multiLearnerData.learners : []);
  const { user, userRole } = useAuth();
  const { toast } = useToast();

  // Debug: Log pour vérifier que le composant se charge

  // Utiliser la fonction utilitaire pour formater les prix en centimes
  const formatPriceDisplay = (priceInCents: number) => {
    return formatPriceFromCents(priceInCents, currency);
  };

  const getTotalPrice = () => {
    const effectiveCount = actualLearnerCount || learnerCount;
    return price * effectiveCount;
  };

  const getDiscountedPrice = () => {
    const effectiveCount = actualLearnerCount || learnerCount;
    // Remise pour les groupes
    if (effectiveCount >= 10) return getTotalPrice() * 0.8; // -20%
    if (effectiveCount >= 5) return getTotalPrice() * 0.85; // -15%
    if (effectiveCount >= 3) return getTotalPrice() * 0.9; // -10%
    return getTotalPrice();
  };

  const getDiscountPercentage = () => {
    const effectiveCount = actualLearnerCount || learnerCount;
    if (effectiveCount >= 10) return 20;
    if (effectiveCount >= 5) return 15;
    if (effectiveCount >= 3) return 10;
    return 0;
  };

  // Handler pour le paiement Stripe immédiat (CB)
  const handleStripePayment = async () => {
    const effectiveUser = user || fallbackUser;

    if (!effectiveUser) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour effectuer un paiement",
        variant: "destructive",
      });
      return;
    }

    // Validation de l'email pour le paiement
    if (!effectiveUser.email) {
      toast({
        title: "Email manquant",
        description: "Une adresse email est requise pour le paiement. Veuillez recharger la page.",
        variant: "destructive",
      });
      return;
    }

    // Validation défensive des props critiques
    if (!formationTitle || price === undefined || price <= 0) {
      console.error('❌ [EnterprisePayment] Données manquantes:', { formationTitle, price, courseId });
      toast({
        title: "Erreur de configuration",
        description: "Les informations de la formation sont incomplètes. Veuillez recharger la page.",
        variant: "destructive",
      });
      return;
    }

    setIsPaymentLoading(true);
    
    // Timeout de sécurité global : force le reset après 15 secondes max
    const loadingTimeout = setTimeout(() => {
      logger.warn('EnterprisePayment loading timeout 15s atteint, force reset');
      setIsPaymentLoading(false);
    }, 15000);

    try {
      const effectiveCount = isEmployeeRegistration && multiLearnerData 
        ? multiLearnerData.learners.length 
        : (actualLearnerCount || learnerCount);
      
      const learnersToSend = isEmployeeRegistration && multiLearnerData 
        ? multiLearnerData.learners
        : savedLearners;

      const { data, error } = await supabase.functions.invoke('create-enterprise-checkout', {
        body: {
          productType: 'course',
          courseId,
          formationTitle,
          amount: Math.round(getDiscountedPrice()),
          currency: currency.toLowerCase(),
          learnerCount: effectiveCount,
          organizationId: effectiveUser.id,
          // Fallback auth pour les utilisateurs sans session (email non confirmé)
          userId: effectiveUser.id,
          userEmail: effectiveUser.email,
          metadata: {
            type: isEmployeeRegistration ? 'enterprise_multi_learner' : 'enterprise',
            originalPrice: price,
            discount: getDiscountPercentage(),
            learners: learnersToSend,
            company: isEmployeeRegistration && multiLearnerData ? multiLearnerData.company : null,
            startDate: isEmployeeRegistration && multiLearnerData ? multiLearnerData.startDate : null
          }
        }
      });


      if (error) {
        console.error('❌ [EnterprisePayment] Stripe error:', error);
        throw new Error(error.message || 'Erreur lors de la création de la session de paiement');
      }

      if (!data?.url) {
        console.error('❌ [EnterprisePayment] Pas d\'URL dans la réponse:', data);
        throw new Error('URL de paiement non reçue');
      }

      const stripeUrl = data.url;

      // Stocker l'URL pour utilisation
      const redirectUrl = stripeUrl;

      // Détecter si on est dans un iframe (preview Lovable)
      let inIframe = false;
      try {
        inIframe = window.self !== window.top;
      } catch (e) {
        inIframe = true; // En cas d'erreur cross-origin, on est probablement dans un iframe
      }


      if (inIframe) {
        // Dans un iframe, tenter d'ouvrir dans un nouvel onglet
        const popup = window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        
        if (!popup) {
          // Popup bloquée
          clearTimeout(loadingTimeout);
          setFallbackPaymentUrl(redirectUrl);
          setIsPaymentLoading(false);
          toast({
            title: "Redirection bloquée",
            description: "Cliquez sur le lien ci-dessus pour accéder au paiement Stripe",
          });
          return;
        } else {
          // Popup ouverte avec succès
          clearTimeout(loadingTimeout);
          setIsPaymentLoading(false);
          toast({
            title: "Paiement ouvert",
            description: "Le paiement Stripe s'est ouvert dans un nouvel onglet",
          });
          return;
        }
      }

      // Hors iframe : tenter la redirection normale
      try {
        window.location.replace(redirectUrl);
      } catch (e) {
        window.location.href = redirectUrl;
      }

      // Si après 1000ms on est toujours sur la page, afficher le fallback ET débloquer l'UI
      setTimeout(() => {
        // Si on arrive ici, c'est que la redirection n'a pas fonctionné
        clearTimeout(loadingTimeout);
        setFallbackPaymentUrl(redirectUrl);
        setIsPaymentLoading(false);
        toast({
          title: "Redirection en attente",
          description: "Si la page ne s'ouvre pas, utilisez le lien affiché",
        });
      }, 1000);
      
    } catch (error: any) {
      // Nettoyer le timeout de sécurité
      clearTimeout(loadingTimeout);
      console.error('❌ [EnterprisePayment] Erreur:', error);
      toast({
        title: "Erreur de paiement",
        description: error.message || "Erreur lors de la création de la session de paiement",
        variant: "destructive",
      });
      // Reset loading UNIQUEMENT en cas d'erreur
      setIsPaymentLoading(false);
    }
  };

  // Handler pour la demande de facture/OPCO (sans paiement immédiat)
  const handleInvoiceRequest = async () => {
    const effectiveUser = user || fallbackUser;
    
    if (!effectiveUser) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour effectuer cette demande",
        variant: "destructive",
      });
      return;
    }

    if (!isEmployeeRegistration || !multiLearnerData) {
      toast({
        title: "Données manquantes",
        description: "Veuillez renseigner les informations des apprenants",
        variant: "destructive",
      });
      return;
    }

    setIsPaymentLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enterprise-payment-webhook', {
        body: {
          type: 'employee_multi_registration',
          formation: {
            id: courseId,
            titre: formationTitle,
            prix_ht: Math.round(price / 1.20),
            prix_ttc: price
          },
          learners: multiLearnerData.learners,
          entreprise: {
            nom: multiLearnerData.company.companyName,
            siret: multiLearnerData.company.siret,
            adresse: multiLearnerData.company.address,
            code_postal: multiLearnerData.company.postalCode,
            ville: multiLearnerData.company.city,
            pays: multiLearnerData.company.country,
            tva: multiLearnerData.company.vatNumber,
            contact_prenom: multiLearnerData.company.contactFirstName,
            contact_nom: multiLearnerData.company.contactLastName,
            contact_email: multiLearnerData.company.contactEmail,
            contact_tel: multiLearnerData.company.contactPhone,
            contact_fonction: multiLearnerData.company.contactPosition,
            billing_address: multiLearnerData.company.address,
            billing_postal_code: multiLearnerData.company.postalCode,
            billing_city: multiLearnerData.company.city,
            billing_country: multiLearnerData.company.country || 'France'
          },
          start_date: multiLearnerData.startDate,
          registered_by_user_id: effectiveUser.id,
          idempotency_key: `emp_multi_${Date.now()}_${effectiveUser.id}`
        }
      });

      if (error) {
        throw new Error(error.message || 'Erreur lors de la création de la demande');
      }

      if (data?.payment_request_id) {
        window.location.href = `/inscription/employee-multi/pending/${data.payment_request_id}`;
        return;
      }
      
    } catch (error: any) {
      console.error('❌ Invoice request error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Erreur lors de la création de la demande",
        variant: "destructive",
      });
    } finally {
      setIsPaymentLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Fallback visible si la redirection automatique échoue */}
      {fallbackPaymentUrl && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded-lg text-center animate-in fade-in">
          <p className="text-amber-800 dark:text-amber-200 mb-3 font-medium">
            La redirection automatique n'a pas fonctionné.
          </p>
          <a 
            href={fallbackPaymentUrl}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Accéder au paiement Stripe
          </a>
        </div>
      )}
      {/* Header Enterprise */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Building2 className="h-6 w-6 text-primary" />
          <Badge variant="outline" className="bg-primary/10">Mode Entreprise</Badge>
        </div>
        <p className="text-muted-foreground font-medium">Formation : {formationTitle}</p>
      </div>

      {/* Sélection du nombre d'apprenants et gestion - FUSIONNÉ (masqué en mode employé) */}
      {!isEmployeeRegistration && (
        <Card className="premium-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Gestion des apprenants
            </CardTitle>
            <CardDescription>
              Sélectionnez le nombre d'apprenants et gérez leur inscription - Prix ajusté automatiquement
            </CardDescription>
          </CardHeader>
        <CardContent className="space-y-6">
          {/* Sélection rapide du nombre */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Nombre d'apprenants</Label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 3, 5, 10].map((count) => (
                <Button
                  key={count}
                  variant={(actualLearnerCount || learnerCount) === count ? "default" : "outline"}
                  onClick={() => {
                    setLearnerCount(count);
                    setActualLearnerCount(count);
                  }}
                  className="relative"
                >
                  {count}
                  {count >= 3 && (
                    <Badge variant="secondary" className="absolute -top-2 -right-2 text-xs">
                      -{count >= 10 ? '20' : count >= 5 ? '15' : '10'}%
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
            
            {/* Contrôles manuels */}
            <div className="flex items-center gap-2 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newCount = Math.max(1, (actualLearnerCount || learnerCount) - 1);
                  setLearnerCount(newCount);
                  setActualLearnerCount(newCount);
                }}
                disabled={(actualLearnerCount || learnerCount) <= 1}
              >
                -
              </Button>
              <span className="px-4 py-2 border rounded text-center min-w-[60px] font-semibold">{actualLearnerCount || learnerCount}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newCount = (actualLearnerCount || learnerCount) + 1;
                  setLearnerCount(newCount);
                  setActualLearnerCount(newCount);
                }}
              >
                +
              </Button>
            </div>
            
            {getDiscountPercentage() > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800 font-medium">
                  <PartyPopper className="h-3 w-3 inline mr-1" /> Remise groupe de {getDiscountPercentage()}% appliquée !
                </p>
                <p className="text-xs text-green-600">
                  Économie de {formatPriceDisplay(getTotalPrice() - getDiscountedPrice())}
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Gestion des apprenants individuels */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Apprenants concernés</Label>
              <Badge variant="outline">{actualLearnerCount || learnerCount} apprenant{(actualLearnerCount || learnerCount) > 1 ? 's' : ''}</Badge>
            </div>
            
            <AddLearnerModal 
              learnerCount={learnerCount}
              preselectedFormationId={courseId}
              preselectedFormationTitle={formationTitle}
              savedLearners={savedLearners}
              onLearnersAdded={(data) => {
                setSavedLearners(data.learners);
                setActualLearnerCount(data.learners.length);
                toast({
                  title: "Apprenants ajoutés",
                  description: `${data.learners.length} apprenant${data.learners.length > 1 ? 's ont' : ' a'} été ajouté${data.learners.length > 1 ? 's' : ''} avec succès.`,
                });
              }} 
            />
            
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <Lightbulb className="h-3 w-3 inline mr-1" /> Astuce : Vous pouvez ajuster le nombre d'apprenants ci-dessus, puis ajouter leurs informations individuelles pour personnaliser leur inscription.
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Prix total mis à jour */}
      <div className="text-center space-y-2">
        <div className="text-4xl font-bold text-primary">{formatPriceDisplay(getDiscountedPrice())}</div>
        {getDiscountPercentage() > 0 && (
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground line-through">{formatPriceDisplay(getTotalPrice())}</div>
            <Badge variant="default" className="bg-green-500">-{getDiscountPercentage()}% remise groupe</Badge>
          </div>
        )}
        <p className="text-sm text-muted-foreground">Pour {actualLearnerCount || learnerCount} apprenant{(actualLearnerCount || learnerCount) > 1 ? 's' : ''}</p>
      </div>

      {/* Options de paiement entreprise */}
      <div className="space-y-4">
        {/* Paiement entreprise immédiat */}
        <Card className="premium-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Paiement entreprise immédiat
            </CardTitle>
            <CardDescription>
              Inscription immédiate de tous les apprenants
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-blue-900">Avantages entreprise :</p>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-blue-600" />
                    Facturation centralisée
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-blue-600" />
                    Dashboard de suivi des apprenants
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-blue-600" />
                    Rapports de progression
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-blue-600" />
                    Support dédié entreprise
                  </li>
                </ul>
              </div>
            </div>
            
            <Button 
              onClick={handleStripePayment} 
              disabled={isPaymentLoading}
              className="w-full"
              size="lg"
            >
              {isPaymentLoading ? "Préparation..." : `Payer ${formatPriceDisplay(getDiscountedPrice())} maintenant`}
            </Button>
            
            {/* Bouton demande de facturation pour inscription employés - MASQUÉ temporairement
               Le flux OPCO/facture n'est pas encore relié au nouveau système de batch.
               À réactiver une fois le flux correctement câblé.
            {isEmployeeRegistration && multiLearnerData && (
              <Button 
                onClick={handleInvoiceRequest} 
                disabled={loading}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <FileText className="h-4 w-4 mr-2" />
                Demander une facturation
              </Button>
            )}
            */}
          </CardContent>
        </Card>


        {/* Demande de financement OPCO */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-500" />
              Financement OPCO Entreprise
            </CardTitle>
            <CardDescription>
              Faire financer votre formation par votre OPCO
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-purple-900">Spécifique entreprise :</p>
                <ul className="space-y-1 text-sm text-purple-800">
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-purple-600" />
                    Convention de formation entreprise
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-purple-600" />
                    Gestion groupée OPCO
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-purple-600" />
                    Suivi administratif dédié
                  </li>
                </ul>
              </div>
            </div>
            
            <Dialog open={showFinancingForm} onOpenChange={setShowFinancingForm}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full rounded-full py-3 border-purple-200 hover:bg-purple-50"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Démarrer demande OPCO entreprise
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Demande de financement OPCO - Entreprise</DialogTitle>
                  <DialogDescription>
                    Demande de financement groupée pour {learnerCount} apprenant{learnerCount > 1 ? 's' : ''}.
                  </DialogDescription>
                </DialogHeader>
                <FinancingRequestForm
                  courseId={courseId}
                  formationTitle={formationTitle}
                  amount={getDiscountedPrice()}
                  currency={currency}
                  learnerCount={learnerCount}
                  isEnterprise={true}
                  isEmployeeRegistration={isEmployeeRegistration}
                  multiLearnerData={multiLearnerData}
                  fallbackUser={fallbackUser}
                  onSuccess={() => {
                    setShowFinancingForm(false);
                    toast({
                      title: "Demande OPCO envoyée",
                      description: `Votre demande pour ${learnerCount} apprenant${learnerCount > 1 ? 's' : ''} a été envoyée avec succès.`,
                    });
                  }}
                />
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}